import { z } from 'zod';
import {
  AVAILABILITY,
  LOCALES,
  MARKUP_TYPE,
  ORDER_STATUS,
  PAYMENT_METHOD,
  PRICING_SCOPE,
  SUPPLIER_PRODUCT_STATUS,
  USER_ROLE,
} from './enums.js';

/** Every list endpoint is paginated. No unbounded findMany anywhere. */
export const PaginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(24),
});
export type PaginationDto = z.infer<typeof PaginationSchema>;

export const LocaleSchema = z.enum(LOCALES);

export const ProductQuerySchema = PaginationSchema.extend({
  locale: LocaleSchema.default('pl'),
  category: z.string().optional(),
  q: z.string().max(200).optional(),
  supplier: z.string().optional(),
  colors: z.array(z.string()).or(z.string().transform((s) => s.split(','))).optional(),
  fabricFamilies: z.array(z.string()).or(z.string().transform((s) => s.split(','))).optional(),
  sizes: z.array(z.string()).or(z.string().transform((s) => s.split(','))).optional(),
  availability: z.enum(AVAILABILITY).optional(),
  /**
   * Only products whose lead image is a real photograph rather than a studio or technical
   * shot. Used for merchandising surfaces, where an unlit bed frame with its slats showing
   * is a bad advertisement for a shop even though it is a perfectly honest picture.
   */
  photographed: z.coerce.boolean().optional(),
  minPrice: z.coerce.number().nonnegative().optional(),
  maxPrice: z.coerce.number().nonnegative().optional(),
  sort: z.enum(['relevance', 'price_asc', 'price_desc', 'newest', 'name']).default('relevance'),
});
export type ProductQueryDto = z.infer<typeof ProductQuerySchema>;

export const AdminProductQuerySchema = PaginationSchema.extend({
  q: z.string().max(200).optional(),
  supplier: z.string().optional(),
  category: z.string().optional(),
  status: z.enum(SUPPLIER_PRODUCT_STATUS).optional(),
  reviewReason: z.string().optional(),
  hasStock: z.coerce.boolean().optional(),
  published: z.coerce.boolean().optional(),
  sort: z.string().default('updatedAt:desc'),
});
export type AdminProductQueryDto = z.infer<typeof AdminProductQuerySchema>;

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
});
export type LoginDto = z.infer<typeof LoginSchema>;

export const RegisterSchema = LoginSchema.extend({
  firstName: z.string().min(1).max(80),
  lastName: z.string().min(1).max(80),
  locale: LocaleSchema.default('pl'),
});
export type RegisterDto = z.infer<typeof RegisterSchema>;

export const RefreshSchema = z.object({ refreshToken: z.string().min(10) });

export const CreateUserSchema = RegisterSchema.extend({ role: z.enum(USER_ROLE).default('customer') });

/**
 * An untouched optional text input submits `""`, not `undefined`. Without this an empty
 * NIP field fails its regex and blocks checkout for every customer who does not want an
 * invoice — with the error attached to a field on a step they can no longer see.
 */
const optionalText = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((v) => (v === '' || v === undefined ? null : v), schema.nullable().optional());

/** Polish address format. NIP optional, and only meaningful for a VAT invoice. */
export const AddressSchema = z.object({
  firstName: z.string().min(1).max(80),
  lastName: z.string().min(1).max(80),
  company: optionalText(z.string().max(160)),
  nip: optionalText(z.string().regex(/^\d{10}$/, 'nip_invalid')),
  street: z.string().min(1).max(160),
  buildingNumber: z.string().min(1).max(20),
  apartmentNumber: optionalText(z.string().max(20)),
  postalCode: z.string().regex(/^\d{2}-\d{3}$/, 'postal_code_invalid'),
  city: z.string().min(1).max(120),
  countryCode: z.string().length(2).default('PL'),
  phone: z.string().regex(/^\+?[0-9 ]{9,15}$/, 'phone_invalid'),
});
export type AddressDto = z.infer<typeof AddressSchema>;

export const CartItemSchema = z.object({
  variantId: z.string().uuid(),
  quantity: z.number().int().min(1).max(99),
});

export const CheckoutSchema = z.object({
  locale: LocaleSchema.default('pl'),
  email: z.string().email(),
  items: z.array(CartItemSchema).min(1),
  shippingAddress: AddressSchema,
  billingAddress: AddressSchema.optional().nullable(),
  wantsInvoice: z.boolean().default(false),
  paymentMethod: z.enum(PAYMENT_METHOD),
  promoCode: z.string().max(40).optional().nullable(),
  acceptsTerms: z.literal(true),
  acceptsWithdrawalInfo: z.literal(true),
  marketingConsent: z.boolean().default(false),
});
export type CheckoutDto = z.infer<typeof CheckoutSchema>;

export const SetCostPriceSchema = z.object({
  supplierProductIds: z.array(z.string().uuid()).min(1).max(500),
  purchasePrice: z.number().positive(),
  currency: z.string().length(3).default('PLN'),
  note: z.string().max(500).optional(),
});

export const PricingRuleSchema = z.object({
  name: z.string().min(1).max(120),
  scope: z.enum(PRICING_SCOPE),
  scopeRef: z.string().optional().nullable(),
  markupType: z.enum(MARKUP_TYPE),
  markupValue: z.number(),
  minMarginPercent: z.number().min(0).max(100).default(0),
  roundingStrategy: z.enum(['none', 'ends_99', 'ends_00', 'nearest_1', 'nearest_5', 'nearest_10']),
  priority: z.number().int(),
  active: z.boolean().default(true),
});

export const OrderStatusChangeSchema = z.object({
  status: z.enum(ORDER_STATUS),
  note: z.string().max(1000).optional(),
});

export const TranslationUpsertSchema = z.object({
  entityType: z.string().min(1),
  entityId: z.string().min(1),
  field: z.string().min(1),
  locale: LocaleSchema,
  value: z.string().max(20000),
  status: z.enum(['draft', 'reviewed']).default('draft'),
});

export const ImportTriggerSchema = z.object({
  feedKey: z.string().min(1),
  dryRun: z.boolean().default(false),
});

export const ReturnRequestSchema = z.object({
  orderId: z.string().uuid(),
  items: z.array(z.object({ orderItemId: z.string().uuid(), quantity: z.number().int().min(1) })).min(1),
  reason: z.string().max(1000),
});

export const ConsentSchema = z.object({
  necessary: z.literal(true),
  analytics: z.boolean().default(false),
  marketing: z.boolean().default(false),
});

/**
 * Manual position entry.
 *
 * A hand-entered product goes through exactly the same canonical path as an imported one:
 * it becomes a `SupplierProduct`, then a `Product` with variants, and it faces the same
 * publish gate. The difference is only where the data came from — a human is authoritative
 * in a way a silent feed is not, so a value entered here is a fact, not a guess.
 *
 * `stockQuantity` is still nullable, and still means "made to order" rather than zero.
 */
export const ParcelInputSchema = z.object({
  index: z.number().int().min(1).max(20),
  heightCm: z.number().positive().nullable(),
  widthCm: z.number().positive().nullable(),
  depthCm: z.number().positive().nullable(),
  weightKg: z.number().positive().nullable(),
});

export const ManualOptionSchema = z.object({
  /** Folded axis slug, e.g. `kolor`, `rozmiar`, `strona`. */
  axisSlug: z.string().min(1).max(60),
  label: z.string().min(1).max(80),
  widget: z.enum(['swatch', 'side', 'size', 'chip', 'toggle']).default('chip'),
  values: z
    .array(
      z.object({
        valueSlug: z.string().min(1).max(80),
        label: z.string().min(1).max(120),
        /** One hex for a solid swatch, two for two-tone, three for multicolour. */
        swatchHex: z.array(z.string().regex(/^#[0-9a-fA-F]{6}$/)).max(3).default([]),
      }),
    )
    .min(1)
    .max(60),
});

export const ManualProductSchema = z.object({
  supplierCode: z.string().min(1).max(40),
  /** Left blank, the server assigns one from the supplier prefix and a counter. */
  supplierSku: z.string().max(80).optional().nullable(),
  ean: z.string().max(20).optional().nullable(),
  name: z.string().min(2).max(300),
  description: z.string().max(20_000).optional().nullable(),
  categorySlug: z.string().min(1).max(80),
  /** Null is legitimate and means the position cannot be published yet. */
  purchasePrice: z.number().positive().nullable(),
  currency: z.string().length(3).default('PLN'),
  /** Null means made to order, exactly as it does for an Adams row. */
  stockQuantity: z.number().int().min(0).nullable(),
  widthCm: z.number().positive().nullable().optional(),
  depthCm: z.number().positive().nullable().optional(),
  heightCm: z.number().positive().nullable().optional(),
  weightKg: z.number().positive().nullable().optional(),
  parcels: z.array(ParcelInputSchema).max(20).default([]),
  imageUrls: z.array(z.string().url()).max(60).default([]),
  attributes: z.record(z.string().max(200)).default({}),
  /** Axes generate the variant matrix. Empty means a single-variant position. */
  options: z.array(ManualOptionSchema).max(5).default([]),
  /** Overrides the computed sale price. Survives every subsequent import. */
  priceOverrideGross: z.number().int().positive().nullable().optional(),
  publishNow: z.boolean().default(false),
});
export type ManualProductDto = z.infer<typeof ManualProductSchema>;

/** Editing an existing position. Every field optional; only what is sent is changed. */
export const ManualProductPatchSchema = ManualProductSchema.partial().omit({
  supplierCode: true,
  options: true,
});
export type ManualProductPatchDto = z.infer<typeof ManualProductPatchSchema>;

/**
 * The manual-upload flow, which is the same job in bulk: upload a file, see the parsed
 * columns and the first ten rows, map them, save the mapping as a reusable feed config.
 */
export const UploadPreviewSchema = z.object({
  filename: z.string().min(1).max(300),
  /** Base64 so the payload survives a JSON body without a multipart dependency. */
  content: z.string().min(1),
  type: z.enum(['csv', 'xlsx']).default('csv'),
  delimiter: z.string().max(3).default(','),
  encoding: z.string().max(20).default('utf-8'),
  /** For xlsx: which sheet. */
  sheet: z.string().max(120).optional(),
});
export type UploadPreviewDto = z.infer<typeof UploadPreviewSchema>;

export const SaveUploadMappingSchema = z.object({
  feedKey: z.string().min(3).max(80).regex(/^[a-z0-9.-]+$/, 'feed_key_invalid'),
  supplierCode: z.string().min(1).max(40),
  /** The full FeedConfig the mapping screen produced, validated on the server. */
  config: z.record(z.unknown()),
  /** Persist the uploaded file so the saved config has a source to re-run against. */
  content: z.string().optional(),
  filename: z.string().max(300).optional(),
  runNow: z.boolean().default(false),
  dryRun: z.boolean().default(true),
});
export type SaveUploadMappingDto = z.infer<typeof SaveUploadMappingSchema>;
